import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/App';

const sinceLabel = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} min`;
  if (mins < 1440) return `${Math.round(mins / 60)} h`;
  return `${Math.round(mins / 1440)} d`;
};

/**
 * One line, app-wide, when the AI is degraded. 3 Sep 2026 — during the budget
 * outage the team learned the AI was down by watching a screen fail. Everyone
 * gets the plain sentence; admins also get the provider detail and the fix.
 */
export const AiStatusBanner = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const poll = () => api.get('/ai-ops/status')
      .then(r => { if (alive) setStatus(r.data); })
      .catch(() => { /* the banner is never the thing that breaks a page */ });
    poll();
    const t = setInterval(poll, 120000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  if (!status?.degraded) return null;
  const isAdmin = ['admin', 'director'].includes(user?.role);

  return (
    <div className="px-4 lg:px-6 pt-4" data-testid="ai-status-banner">
      <div className="rounded-ui-lg border border-status-warn/30 bg-status-warn-soft px-4 py-3
        flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-status-warn shrink-0 mt-1" />
        <div className="min-w-0">
          <p className="text-body text-ink-1" data-testid="ai-status-message">
            {status.message}
          </p>
          {status.since && (
            <p className="text-caption text-ink-3 mt-1">
              Unavailable for the last {sinceLabel(status.since)}.
            </p>
          )}
          {isAdmin && status.detail && (
            <p className="text-caption text-ink-2 mt-2" data-testid="ai-status-admin-detail">
              {status.detail.hint} {status.detail.failures} failed calls,
              last in {status.detail.last_where || 'unknown'}.{' '}
              <Link to="/app/admin/api-usage" className="underline underline-offset-2 text-ink-1"
                data-testid="ai-status-usage-link">
                Open API Usage
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiStatusBanner;
